/**
 * Results page – display quiz outcome, filter products, buy wrapper, subscription.
 * Loaded only on page.results template.
 * Depends on quiz-engine.js (window.MillsQuizEngine).
 * Quiz answers are read only via quizEngine.loadQuizDataFromFlat() (localStorage).
 * Variant payloads on .prod use jQuery .data("variation1" | "variation2") objects (id, price in cents, qtyTitle, etc.).
 */
(function () {
  "use strict";

  const quizEngine = window.MillsQuizEngine;
  if (!quizEngine) return;
  const quizValues = quizEngine.VALUES;

  /**
   * Mills 14.10.2026: one margin for both pack choice and the "no recipe fits" check.
   * 0 = a pack counts only if it is at least the dog's daily need in grams.
   * 0.05 = a pack up to 5% smaller than the need still counts.
   */
  const TOLERANCE = 0;

  /** @type {{ handle: string, $product: jQuery }|null} */
  let dualFlavorSlot1 = null;
  /** @type {{ handle: string, $product: jQuery }|null} */
  let dualFlavorSlot2 = null;

  const SELECTORS = {
    wrapper: "#results-wrapper",
    sidesLeft: "#results-sides-left",
    resultsProds: ".results-prods",
    prodRow: ".prod-row",
    sampleRow: ".sample-row",
    prod: ".prod",
    resultsFormWrapper: "#results-form-wrapper",
    resultsStickyGoto: "#results-sticky-goto",
    resultsTitle: "#results-title",
    resultsSubtitle: "#results-subtitle",
    headlineOnRight: ".headline .on-right",
    contactFormQuizData: "#ContactFormQuizData",
    resultsBuyWrapperOption1: "#results-buy-wrapper-option-1",
    resultsBuyWrapperOption2: "#results-buy-wrapper-option-2",
    resultsBuyWrapperPrice: "#results-buy-wrapper-price",
    resultsBuyWrapperVariationId: "#results-buy-wrapper-variation-id",
    subscriptionPrices: "#subscription-prices",
    originalMonthlyPrice: "#original-monthly-price",
    originalDailyPrice: "#original-daily-price",
    subscriptionMonthlyPrice: "#subscription-monthly-price",
    subscriptionDailyPrice: "#subscription-daily-price",
    samplePack: "#sample-pack",
    subscriptionOption: "[data-option='subscription']",
    dataOption: "[data-option]",
    quizResultsAdd2: "#quiz-results-add-2",
    cartNote: "#CartNote",
    htbWrapper: ".htb-wrapper",
    sidesLeftTitle: "#results-sides-left .title",
    resultsProdsProd: ".results-prods .prod",
    resultsProdsProdRow: ".results-prods .prod-row",
    resultsProdsSampleRow: ".results-prods .sample-row",
    openClose: ".results-prods .prod .open-close",
    activeShowProd: ".show.active .prod",
    calculatName: ".the-name",
    calculatType: ".the-type",
    calculatAge: ".the-age",
    calculatWeight: ".the-weight",
    calculatCals: ".the-cals",
    productSize: ".size",
    productMonthlyPrice: ".monthly-price",
    productDailyPrice: ".daily-price",
    oneTimeOptions: ".one-time-options",
    qtyButtons: "#results-buy-wrapper-qty-buttons .qty-button",
    qtyButton: ".qty-button",
    productFormMain: "#product-form-main",
    subscriptionDescriptionInput: "#results-subscription-description",
    subscriptionFlagInput: "#results-subscription-flag",
    subscriptionFrequencyValue: "#subscription-frequency-value",
    subscriptionFrequencyProp: "#results-subscription-frequency-prop",
    quizDataProp: "#results-quiz-data-prop",
    subscriptionBuyOptions: ".subscription-buy-options",
    subscriptionSizeButtons: "#subscription-size-buttons .subscription-size-btn",
    subscriptionFrequencyButtons: "#subscription-frequency-buttons .subscription-frequency-btn",
    subscriptionSizeBtnPrimary: "#subscription-size-btn-primary",
    subscriptionSizeBtnSecondary: "#subscription-size-btn-secondary",
    addToCart: "#AddToCart",
    quizResultsAdd2Btn: "#quiz-results-add-2",
    dualFlavorBlock: "#results-dual-flavor-block",
    dualFlavor1Title: "#results-dual-flavor-1-title",
    dualFlavor2Title: "#results-dual-flavor-2-title",
    productTitle: ".product-title",
  };

  /** jQuery .data() keys for primary/secondary variant payloads on .prod nodes. */
  const VARIATION_DATA_KEYS = {
    primary: "variation1",
    secondary: "variation2",
  };

  const CLS = {
    active: "active",
    show: "show",
    expanded: "expanded",
    hideOnD: "hide-on-d",
    headlineLoading: "results-headline--loading",
    samplePack: "sample-pack",
    flavorRow1: "results-flavor-row-1",
    flavorRow2: "results-flavor-row-2",
  };

  const PRICING = {
    centsPerShekel: 100,
    daysInMonth: 30,
    subscriptionMultiplier: 0.9,
    shekelSymbol: "₪",
  };

  const FILTER = {
    tagPrefix: "t-",
    dogCollectionClass: "c-כלבים",
    microGrainTag: "מיקרו-גרגיר",
    superFoodTag: "סופר-פוד",
    seniorsTag: "מבוגרים",
    bestSellerTag: "best-seller",
    microGrainMaxWeight: 4,
    microGrainExcludeAbove: 7,
    superFoodMinWeight: 10,
    seniorsMinAge: 10,
    excludedTags: ["גורים", "טעימות", "חטיפים", "טיפוח", "אביזרים"],
  };

  const LAYOUT = {
    scrollToSidebarOffset: 100,
  };

  const GTAG = {
    eventName: "purchase_option_selected",
    subscriptionCategory: "subscription_purchase_option",
    oneTimeCategory: "one_time_purchase_option",
  };

  const REQUEST = {
    productJsonPathPrefix: "/products/",
    productJsonPathSuffix: ".js",
  };
  const LIMITS = {
    lineItemPropertyMaxChars: 900,
  };
  const API = {
    /*
     * The PUBLIC quiz-save route. The legacy /shopify/dog/save-quiz-dog sits behind the
     * server-to-server api.secret, so from a browser it answers 401 and the quiz saved
     * nothing — /storefront/quiz-dogs is its anonymous counterpart (throttled server-side).
     */
    baseUrl: "https://mills.lets.co.il/storefront",
    saveQuizDogPath: "/quiz-dogs",
  };
  const STORAGE_KEYS = {
    quizDogId: "mills_quiz_dog_id",
  };
  /**
   * Mills 14.10.2026: the subscription product has more than 250 variants and Liquid returns at most 250,
   * so the SKU map is read from the product.sub-map template, one page at a time.
   */
  const SUBSCRIPTION_MAP = {
    productHandle: "subs-test",
    view: "sub-map",
  };
  /** @type {Object<string, { id: string, price: number }>|null} */
  let subscriptionMapAll = null;
  const LINE_ITEM_PROPERTIES = {
    dogId: "כלב",
  };

  const DATA = {
    attrHandle: "data-handle",
    attrMultiplier: "data-multiplier",
    attrOption: "data-option",
    attrId: "data-id",
    attrPrice: "data-price",
    attrSubscriptionBySku: "data-subscription-by-sku",
    attrSubscriptionPack: "data-subscription-pack",
    attrFrequencyValue: "data-frequency-value",
    optionSubscription: "subscription",
  };

  const TEXT = {
    expandList: "+ לרשימה מלאה",
    collapseList: "- לסגירת רשימה",
    noRecTitleMaleSuffix: " זקוק להתאמה אישית נוספת",
    noRecTitleFemaleSuffix: " זקוקה להתאמה אישית נוספת",
    noRecSubtitleHtml:
      "השאירו פרטים ונחזור אליכם בהקדם עם תכנית<br>ייעודית על בסיס השאלון שביצעתם.",
    petTypePrefix: "ל",
  };

  const FORMAT = {
    sizePackSuffix: " גרם לאריזה",
    monthlySuffix: " לחודש",
    dailySuffix: " ליום",
    weightUnitSuffix: " ק״ג",
    cartNoteSensitivitiesPrefix: "רגישויות: ",
    cartNoteNewline: "\n",
    contactFormCaloriesPrefix: "קלוריות: ",
    contactFormFieldSep: ", ",
    contactFormNamePrefix: "שם: ",
    contactFormGenderPrefix: "מין: ",
    contactFormTypePrefix: "סוג: ",
    contactFormAgePrefix: "גיל: ",
    contactFormWeightPrefix: "משקל: ",
    contactFormCastrationPrefix: "סירוס: ",
    contactFormActivityPrefix: "פעילות גופנית: ",
    contactFormBodyPrefix: "מבנה גוף: ",
    contactFormProteinPrefix: "העדפות חלבון: ",
    contactFormSensitivitiesPrefix: "רגישויות: ",
  };

  const GRAMS_FROM_SKU_PATTERN = /[^0-9.]+/g;
  /** SKU of a 30-pack variant, e.g. "SF30 - אריזה יומית של 499 גרם". */
  const THIRTY_PACK_SKU_PATTERN = /^[A-Z]+30(?![0-9])/;

  const SCROLL_ANIMATION = {
    durationMs: 1000,
    easing: "easeInOutQuint",
  };

  const PRICE_RENDER_SCOPE = {
    all: "all",
    toggle: "toggle",
  };

  const $cache = {};
  let quizDogId = null;

  function normalizeDogId(rawId) {
    if (rawId == null) return "";
    const text = String(rawId).trim();
    if (!text) return "";
    const match = text.match(/(\d+)$/);
    return match && match[1] ? match[1] : text;
  }

  function setQuizDogId(rawId) {
    const normalized = normalizeDogId(rawId);
    if (!normalized) return "";
    quizDogId = normalized;
    try {
      window.localStorage.setItem(STORAGE_KEYS.quizDogId, normalized);
    } catch (_e) {
      /* storage unavailable */
    }
    return normalized;
  }

  function loadQuizDogIdFromStorage() {
    try {
      const fromStorage = window.localStorage.getItem(STORAGE_KEYS.quizDogId);
      if (!fromStorage) return "";
      return setQuizDogId(fromStorage);
    } catch (_e) {
      return "";
    }
  }

  function extractDogIdFromResponse(payload) {
    if (!payload || typeof payload !== "object") return "";
    return (
      payload.id ||
      payload.dogId ||
      payload.quizDogId ||
      payload.dog ||
      payload.quizDog ||
      (payload.data &&
        (payload.data.id ||
          payload.data.dogId ||
          payload.data.quizDogId ||
          payload.data.dog ||
          payload.data.quizDog)) ||
      ""
    );
  }

  function buildSaveQuizPayload(formattedData, calories) {
    var today = new Date();
    var startDate = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, "0"),
      String(today.getDate()).padStart(2, "0"),
    ].join("-");
    return {
      discount: 0.9,
      interval: 1,
      doubleFood: false,
      status: "new-onboarding",
      customerId: null,
      hkId: "0",
      startDate: startDate,
      last: "",
      dogs: [
        {
          avatar: 2,
          status: "active",
          name: formattedData.dogs[0].name,
          sex: formattedData.dogs[0].quizData.sex,
          caloriesPerDay: calories || 0,
          variants: [],
          subPrice: 0,
          isTrial: false,
          fullPrice: 0,
          trialPrice: 0,
          noTrialFirstOrderPrice: 0,
          id: "",
          quizData: formattedData.dogs[0].quizData,
        },
      ],
    };
  }

  async function syncQuizDogIdToCart(quizDogId) {
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: { quiz_dog_id: quizDogId } }),
      });
    } catch (e) {
      console.error("Failed to sync quiz_dog_id to cart:", e);
    }
  }

  async function createDogFromQuizData(parsedQuizData) {
    if (!parsedQuizData || typeof parsedQuizData !== "object") return "";
    if (!quizEngine.formatMillsQuizData || typeof quizEngine.formatMillsQuizData !== "function") return "";
    var formattedData = quizEngine.formatMillsQuizData(parsedQuizData);
    if (!formattedData || typeof formattedData !== "object") return "";
    var calories = quizEngine.computeCalories ? quizEngine.computeCalories(parsedQuizData) : 0;
    var payload = buildSaveQuizPayload(formattedData, calories);
    try {
      var response = await fetch(API.baseUrl + API.saveQuizDogPath, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        console.error("save-quiz-dog failed:", response.status, response.statusText);
        return "";
      }
      var json = await response.json().catch(function () {
        return {};
      });
      var returnedId = extractDogIdFromResponse(json);
      var savedId = setQuizDogId(returnedId);
      if (savedId) {
        syncQuizDogIdToCart(savedId);
      }
      return savedId;
    } catch (e) {
      console.error("createDogFromQuizData error:", e);
      return "";
    }
  }

  /**
   * Caches stable jQuery references used repeatedly after DOM ready.
   * @returns {void}
   */
  function cacheStaticDom() {
    $cache.wrapper = $(SELECTORS.wrapper);
    $cache.headlineOnRight = $(SELECTORS.headlineOnRight);
    $cache.samplePack = $(SELECTORS.samplePack);
    $cache.buyPrice = $(SELECTORS.resultsBuyWrapperPrice);
    $cache.buyVariationId = $(SELECTORS.resultsBuyWrapperVariationId);
    $cache.buyOption1 = $(SELECTORS.resultsBuyWrapperOption1);
    $cache.buyOption2 = $(SELECTORS.resultsBuyWrapperOption2);
    $cache.htbWrapper = $(SELECTORS.htbWrapper);
    $cache.sidesLeftTitle = $(SELECTORS.sidesLeftTitle);
    $cache.subscriptionOption = $(SELECTORS.subscriptionOption);
    $cache.originalMonthlyPrice = $(SELECTORS.originalMonthlyPrice);
    $cache.originalDailyPrice = $(SELECTORS.originalDailyPrice);
    $cache.subMonthlyPrice = $(SELECTORS.subscriptionMonthlyPrice);
    $cache.subDailyPrice = $(SELECTORS.subscriptionDailyPrice);
    $cache.oneTimeOptions = $(SELECTORS.oneTimeOptions);
    $cache.quizResultsAdd2 = $(SELECTORS.quizResultsAdd2);
    $cache.subscriptionPrices = $(SELECTORS.subscriptionPrices);
    $cache.productFormMain = $(SELECTORS.productFormMain);
    $cache.subscriptionDescriptionInput = $(SELECTORS.subscriptionDescriptionInput);
    $cache.subscriptionFlagInput = $(SELECTORS.subscriptionFlagInput);
    $cache.subscriptionFrequencyValue = $(SELECTORS.subscriptionFrequencyValue);
    $cache.subscriptionFrequencyProp = $(SELECTORS.subscriptionFrequencyProp);
    $cache.quizDataPropInput = $(SELECTORS.quizDataProp);
    $cache.dogIdPropInput = $("#results-dog-id-prop");
    $cache.subscriptionBuyOptions = $(SELECTORS.subscriptionBuyOptions);
    $cache.subscriptionSizeBtnPrimary = $(SELECTORS.subscriptionSizeBtnPrimary);
    $cache.subscriptionSizeBtnSecondary = $(SELECTORS.subscriptionSizeBtnSecondary);
    $cache.addToCart = $(SELECTORS.addToCart);
    $cache.quizResultsAdd2Btn = $(SELECTORS.quizResultsAdd2Btn);
    $cache.dualFlavorBlock = $(SELECTORS.dualFlavorBlock);
  }

  /**
   * Builds compact JSON string for hidden line item `_quiz_data` property.
   * Falls back to empty string when quiz data cannot be reconstructed.
   * @returns {string}
   */
  function buildQuizDataPropertyValue() {
    if (!quizEngine.loadQuizDataFromFlat || typeof quizEngine.loadQuizDataFromFlat !== "function") return "";
    if (!quizEngine.formatMillsQuizData || typeof quizEngine.formatMillsQuizData !== "function") return "";
    const parsedQuizData = quizEngine.loadQuizDataFromFlat();
    if (!parsedQuizData || typeof parsedQuizData !== "object") return "";
    const formattedData = quizEngine.formatMillsQuizData(parsedQuizData);
    if (!formattedData || !formattedData.dogs || !formattedData.dogs[0]) return "";
    const calories = quizEngine.computeCalories ? quizEngine.computeCalories(parsedQuizData) : 0;
    const payload = buildSaveQuizPayload(formattedData, calories);
    if (!payload || typeof payload !== "object") return "";
    let serialized = "";
    try {
      serialized = JSON.stringify(payload);
    } catch (_e) {
      return "";
    }
    if (serialized.length <= LIMITS.lineItemPropertyMaxChars) return serialized;
    if (payload.dogs && payload.dogs[0] && payload.dogs[0].quizData) {
      payload.dogs[0].quizData.notes = "";
      try {
        serialized = JSON.stringify(payload);
      } catch (_e) {
        return "";
      }
    }
    if (serialized.length > LIMITS.lineItemPropertyMaxChars) {
      return serialized.slice(0, LIMITS.lineItemPropertyMaxChars);
    }
    return serialized;
  }

  /**
   * True when the subscription purchase option is active in the buy wrapper.
   * @returns {boolean}
   */
  function isSubscriptionActive() {
    return $cache.subscriptionOption.hasClass(CLS.active);
  }

  /**
   * Formats an amount with the shekel symbol (no thousands separator).
   * @param {string|number} amount
   * @returns {string}
   */
  function shekel(amount) {
    return PRICING.shekelSymbol + amount;
  }

  /**
   * Pure price breakdown from a base monthly shekel amount (before subscription discount).
   * @param {string|number} shekelsInput Base shekels; may be string from data-price to preserve formatting.
   * @param {boolean} isSubscription
   * @returns {{ display: string, originalMonthly: string, originalDaily: string, subMonthly: string, subDaily: string }}
   */
  function computePrices(shekelsInput, isSubscription) {
    const n = parseFloat(shekelsInput);
    const display = isSubscription
      ? (n * PRICING.subscriptionMultiplier).toFixed(0)
      : typeof shekelsInput === "string" && shekelsInput !== ""
        ? shekelsInput
        : n.toFixed(0);
    const originalMonthly = n.toFixed(0);
    const originalDaily = (n / PRICING.daysInMonth).toFixed(2);
    const subDaily = isSubscription
      ? (parseFloat(display) / PRICING.daysInMonth).toFixed(2)
      : originalDaily;
    return {
      display: display,
      originalMonthly: originalMonthly,
      originalDaily: originalDaily,
      subMonthly: display,
      subDaily: subDaily,
    };
  }

  /**
   * Writes price labels; scope "all" updates original + main + subscription rows, "toggle" only main + subscription.
   * @param {{ display: string, originalMonthly?: string, originalDaily?: string, subMonthly: string, subDaily: string }} prices
   * @param {"all"|"toggle"} scope
   * @returns {void}
   */
  function renderPrices(prices, scope) {
    $cache.buyPrice.text(shekel(prices.display));
    $cache.subMonthlyPrice.text(shekel(prices.subMonthly) + FORMAT.monthlySuffix);
    $cache.subDailyPrice.text(shekel(prices.subDaily) + FORMAT.dailySuffix);
    if (scope === PRICE_RENDER_SCOPE.all) {
      $cache.originalMonthlyPrice.text(shekel(prices.originalMonthly));
      $cache.originalDailyPrice.text(shekel(prices.originalDaily));
    }
  }

  /**
   * Shows or hides one-time vs subscription blocks (init and after mode click).
   * @param {boolean} isSubscription
   * @returns {void}
   */
  function setSubscriptionMode(isSubscription) {
    const $scope = $cache.wrapper;
    if (isSubscription) {
      $scope.find(SELECTORS.oneTimeOptions).hide();
      $cache.quizResultsAdd2.hide();
      $cache.subscriptionBuyOptions.show();
    } else {
      $scope.find(SELECTORS.oneTimeOptions).show();
      $cache.quizResultsAdd2.show();
      $cache.subscriptionBuyOptions.hide();
    }
    updateDualFlavorBlockVisibility();
  }

  /**
   * Syncs hidden line-item properties for add-to-cart by purchase mode.
   * @returns {void}
   */
  function syncSubscriptionLineItemProperties() {
    const quizDataPropertyValue = buildQuizDataPropertyValue();
    const isSubscription = isSubscriptionActive();
    if (isSubscription) {
      let desc = "מנוי חודשי מתחדש";
      if (isDualFlavorSubscriptionMode() && dualFlavorSlot1 && dualFlavorSlot2) {
        desc +=
          " — 2×15 טעמים: " +
          getProductTitleFromRow(dualFlavorSlot1.$product) +
          " + " +
          getProductTitleFromRow(dualFlavorSlot2.$product);
      }
      $cache.subscriptionDescriptionInput.val(desc);
      $cache.subscriptionFlagInput.val("true");
      const freq = ($cache.subscriptionFrequencyValue.val() || "Monthly").trim();
      const freqHebrew = freq === "Two months" ? "דו־חודשי" : "חודשי";
      $cache.subscriptionFrequencyProp.val(freqHebrew);
      if ($cache.dogIdPropInput && $cache.dogIdPropInput.length) {
        $cache.dogIdPropInput.val(quizDogId ? String(quizDogId) : "");
      }
      if ($cache.quizDataPropInput && $cache.quizDataPropInput.length) {
        $cache.quizDataPropInput.val(quizDataPropertyValue);
      }
    } else {
      $cache.subscriptionDescriptionInput.val("");
      $cache.subscriptionFlagInput.val("");
      $cache.subscriptionFrequencyProp.val("");
      if ($cache.dogIdPropInput && $cache.dogIdPropInput.length) {
        $cache.dogIdPropInput.val("");
      }
      if ($cache.quizDataPropInput && $cache.quizDataPropInput.length) {
        $cache.quizDataPropInput.val("");
      }
    }
  }

  /**
   * Builds a normalized variant entry used for calorie / grams matching.
   * @param {object} variant Shopify variant object from product JSON.
   * @param {number} gramsBenchmark
   * @param {number|string} multiplier
   * @returns {object}
   */
  function buildVariantEntry(variant, gramsBenchmark, multiplier) {
    const sku = variant.sku;
    const gramsRaw = sku.split("-")[1];
    const grams = parseFloat(String(gramsRaw).replace(GRAMS_FROM_SKU_PATTERN, ""));
    const calories = grams * multiplier;
    return {
      id: variant.id,
      available: variant.available,
      price: variant.price,
      grams: grams,
      gramsBenchmark: gramsBenchmark,
      calories: calories,
      diff: Math.abs(grams - gramsBenchmark),
      qtyTitle: variant.option2,
      name: variant.name,
      sku: variant.sku || "",
    };
  }

  /**
   * Splits variants into lists above and below the benchmark, each sorted by closeness to benchmark.
   * @param {Array<object>} variants
   * @param {number} gramsBenchmark
   * @param {number|string} multiplier
   * @returns {{ higher: Array<object>, lower: Array<object> }}
   */
  function packRank(entry) {
    return THIRTY_PACK_SKU_PATTERN.test(String(entry.sku || "")) ? 0 : 1;
  }

  function splitVariantsByBenchmark(variants, gramsBenchmark, multiplier) {
    const higher = [];
    const lower = [];
    const coverThreshold = gramsBenchmark * (1 - TOLERANCE);
    for (let i = 0; i < variants.length; i++) {
      const entry = buildVariantEntry(variants[i], gramsBenchmark, multiplier);
      if (entry.grams >= coverThreshold) higher.push(entry);
      else lower.push(entry);
    }
    // Mills 14.10.2026: when two packs are equally close (same grams), the 30-pack comes first,
    // whatever order the variants are listed in on the product.
    const byDiffThenPack = function (a, b) {
      if (a.diff !== b.diff) return a.diff - b.diff;
      return packRank(a) - packRank(b);
    };
    higher.sort(byDiffThenPack);
    lower.sort(byDiffThenPack);
    return { higher: higher, lower: lower };
  }

  /**
   * Picks the best primary/secondary pair from a sorted variant list if at least one variant is available.
   * @param {Array<object>} sortedVariants
   * @returns {{ primary: object, secondary: object|undefined }|null}
   */
  function pickBestAvailablePair(sortedVariants) {
    if (sortedVariants.length === 0) return null;
    const primary = sortedVariants[0];
    const secondary = sortedVariants[1];
    const isAvailable =
      sortedVariants.length === 1 ? primary.available : primary.available || (secondary && secondary.available);
    if (!isAvailable) return null;
    return { primary: primary, secondary: secondary };
  }

  /**
   * Populates a product row from variant data and shows the row.
   * @param {jQuery} $product
   * @param {Array<object>} sortedVariants
   * @param {number} recommendedProductCount
   * @returns {boolean} True if a variant was applied.
   */
  function tryApplyVariantFromSorted($product, sortedVariants, recommendedProductCount) {
    const pair = pickBestAvailablePair(sortedVariants);
    if (!pair) return false;
    updateRowFromVariant($product, pair.primary, pair.secondary, recommendedProductCount);
    $product.parents(SELECTORS.prodRow).addClass(CLS.show);
    return true;
  }

  /**
   * Fetches product JSON and applies the best-fit variant for the calorie target.
   * @param {jQuery} $product
   * @param {number} recommendedProductCount
   * @param {number} calories
   * @returns {void}
   */
  function fetchAndApplyBestVariant($product, recommendedProductCount, calories) {
    const handle = $product.attr(DATA.attrHandle);
    const multiplier = $product.attr(DATA.attrMultiplier);
    const url = REQUEST.productJsonPathPrefix + handle + REQUEST.productJsonPathSuffix;

    return new Promise(function (resolve) {
      $.getJSON(url, function (product) {
        const gramsBenchmark = Math.ceil(calories / multiplier);
        const split = splitVariantsByBenchmark(product.variants, gramsBenchmark, multiplier);
        const higherFiltered = filterVariantEntriesBySubscriptionMap($product, split.higher);
        const lowerFiltered = filterVariantEntriesBySubscriptionMap($product, split.lower);
        let variantApplied = false;

        // Mills 14.10.2026: a recipe whose largest pack is smaller than the dog's need is not shown.
        // The fallback to a smaller pack stays only when a big-enough pack exists but is unavailable.
        if (higherFiltered.length > 0) {
          variantApplied = tryApplyVariantFromSorted($product, higherFiltered, recommendedProductCount);
          if (!variantApplied && lowerFiltered.length > 0) {
            variantApplied = tryApplyVariantFromSorted($product, lowerFiltered, recommendedProductCount);
          }
        }
        resolve(variantApplied);
      }).fail(function () {
        resolve(false);
      });
    });
  }

  /**
   * Writes primary (and optional secondary) variant payloads onto the product row via jQuery .data().
   * @param {jQuery} $product
   * @param {object} primaryVariant
   * @param {object} [secondaryVariant]
   * @returns {void}
   */
  function writeVariantToRow($product, primaryVariant, secondaryVariant) {
    $product.find(SELECTORS.productSize).text(primaryVariant.grams + FORMAT.sizePackSuffix);
    $product.data(VARIATION_DATA_KEYS.primary, {
      id: primaryVariant.id,
      price: primaryVariant.price,
      qtyTitle: primaryVariant.qtyTitle,
      grams: primaryVariant.grams,
      name: primaryVariant.name,
      sku: primaryVariant.sku || "",
    });
    $product
      .find(SELECTORS.productMonthlyPrice)
      .text(shekel(primaryVariant.price / PRICING.centsPerShekel) + FORMAT.monthlySuffix);
    $product
      .find(SELECTORS.productDailyPrice)
      .text(
        shekel((primaryVariant.price / PRICING.centsPerShekel / PRICING.daysInMonth).toFixed(2)) + FORMAT.dailySuffix
      );

    if (
      secondaryVariant &&
      secondaryVariant.available &&
      secondaryVariant.grams === primaryVariant.grams
    ) {
      $product.data(VARIATION_DATA_KEYS.secondary, {
        id: secondaryVariant.id,
        price: secondaryVariant.price,
        qtyTitle: secondaryVariant.qtyTitle,
        sku: secondaryVariant.sku || "",
      });
    } else {
      $product.removeData(VARIATION_DATA_KEYS.secondary);
    }
  }

  /**
   * Reads and validates a variant object from .data(variationKey). Returns normalized shape for buy-wrapper or null.
   * @param {jQuery} $el
   * @param {string} variationKey VARIATION_DATA_KEYS value
   * @returns {{ id: *, priceInCents: number, qtyTitle: string }|null}
   */
  function parseVariantAttr($el, variationKey) {
    const d = $el.data(variationKey);
    if (!d || typeof d !== "object") return null;
    const id = d.id;
    const price = d.price;
    const qtyTitle = d.qtyTitle;
    if (id === undefined || id === null || String(id).trim() === "") return null;
    if (!isFinite(price)) return null;
    const label = qtyTitle == null ? "" : String(qtyTitle).trim();
    if (label === "") return null;
    const skuRaw = d.sku;
    return {
      id: id,
      priceInCents: price,
      qtyTitle: label,
      sku: skuRaw == null ? "" : String(skuRaw).trim(),
    };
  }

  /**
   * Parses `data-subscription-by-sku` into sku → { id, price } (Shopify cents for price).
   * @param {jQuery} $product
   * @returns {Object<string, { id: string, price: number }>}
   */
  function parseSubscriptionBySkuAttr($product) {
    if (subscriptionMapAll) return subscriptionMapAll;
    const raw = $product.attr(DATA.attrSubscriptionBySku);
    const map = {};
    if (!raw || typeof raw !== "string") return map;
    const segments = raw.split("||");
    for (let i = 0; i < segments.length; i++) {
      const parts = segments[i].split("|");
      if (parts.length < 3) continue;
      const sku = parts[0];
      const id = parts[1];
      const price = parseInt(parts[2], 10);
      if (!sku || id === undefined || id === null || !isFinite(price)) continue;
      map[sku] = { id: id, price: price };
    }
    return map;
  }

  /**
   * Keeps only variant entries whose SKU exists on `data-subscription-by-sku` (subs-test mapping).
   * @param {jQuery} $product
   * @param {Array<object>} sortedEntries buildVariantEntry rows
   * @returns {Array<object>}
   */
  function filterVariantEntriesBySubscriptionMap($product, sortedEntries) {
    const map = parseSubscriptionBySkuAttr($product);
    if (!sortedEntries || sortedEntries.length === 0) return [];
    return sortedEntries.filter(function (entry) {
      const sku = entry.sku != null ? String(entry.sku).trim() : "";
      return sku && Object.prototype.hasOwnProperty.call(map, sku);
    });
  }

  /**
   * @param {jQuery} $product
   * @returns {boolean}
   */
  /**
   * Loads every page of the subscription SKU map (sku -> { id, price in cents }).
   * @returns {Promise<void>}
   */
  function loadSubscriptionMap() {
    const base = REQUEST.productJsonPathPrefix + SUBSCRIPTION_MAP.productHandle + "?view=" + SUBSCRIPTION_MAP.view + "&page=";
    const map = {};
    function readPage(page) {
      return fetch(base + page, { credentials: "same-origin" })
        .then(function (r) {
          return r.text();
        })
        .then(function (text) {
          const data = JSON.parse(text.trim());
          (data.items || []).forEach(function (item) {
            const sku = item[0] == null ? "" : String(item[0]);
            if (!sku) return;
            const entry = { id: String(item[1]), price: parseInt(item[2], 10) };
            map[sku] = entry;
            map[sku.trim()] = entry;
          });
          return data.pages || 1;
        });
    }
    return readPage(1)
      .then(function (pages) {
        const rest = [];
        for (let p = 2; p <= pages; p++) rest.push(readPage(p));
        return Promise.all(rest);
      })
      .then(function () {
        if (Object.keys(map).length) subscriptionMapAll = map;
      })
      .catch(function () {});
  }

  function hasSubscriptionMapping($product) {
    const m = parseSubscriptionBySkuAttr($product);
    return Object.keys(m).length > 0;
  }

  /**
   * @returns {"primary"|"secondary"}
   */
  function getActiveSubscriptionPack() {
    const $active = $("#subscription-size-buttons .subscription-size-btn." + CLS.active);
    const role = $active.attr(DATA.attrSubscriptionPack);
    return role === "secondary" ? "secondary" : "primary";
  }

  /**
   * @param {jQuery} $product
   * @param {"primary"|"secondary"} pack
   * @returns {{ id: string, price: number }|null}
   */
  function getSubscriptionVariantForPack($product, pack) {
    const key = pack === "secondary" ? VARIATION_DATA_KEYS.secondary : VARIATION_DATA_KEYS.primary;
    const d = $product.data(key);
    if (!d || typeof d !== "object") return null;
    const sku = d.sku != null ? String(d.sku).trim() : "";
    if (!sku) return null;
    const map = parseSubscriptionBySkuAttr($product);
    return map[sku] || null;
  }

  function isDualFlavorSubscriptionMode() {
    return (
      isSubscriptionActive() &&
      getActiveSubscriptionPack() === "secondary" &&
      $cache.subscriptionSizeBtnSecondary.length > 0 &&
      $cache.subscriptionSizeBtnSecondary.is(":visible")
    );
  }

  function clearDualFlavorSlots() {
    dualFlavorSlot1 = null;
    dualFlavorSlot2 = null;
  }

  function getProductTitleFromRow($product) {
    const t = $product.find(SELECTORS.productTitle).first().text();
    return (t || "").replace(/\s+/g, " ").trim() || "—";
  }

  function updateDualFlavorBlockVisibility() {
    if (!$cache.dualFlavorBlock || !$cache.dualFlavorBlock.length) return;
    const show = isDualFlavorSubscriptionMode();
    if (show) {
      $cache.dualFlavorBlock.removeAttr("hidden").show();
    } else {
      $cache.dualFlavorBlock.attr("hidden", "hidden").hide();
    }
  }

  function getDualFlavorSubscriptionShekelsTotal() {
    if (!dualFlavorSlot1 || !dualFlavorSlot2) return null;
    const p1 = getSubscriptionVariantForPack(dualFlavorSlot1.$product, "secondary");
    const p2 = getSubscriptionVariantForPack(dualFlavorSlot2.$product, "secondary");
    if (!p1 || !p2 || !isFinite(p1.price) || !isFinite(p2.price)) return null;
    return (p1.price + p2.price) / PRICING.centsPerShekel;
  }

  function getDualFlavorOriginalShekelsTotal() {
    if (!dualFlavorSlot1 || !dualFlavorSlot2) return null;
    const r1 = parseVariantAttr(dualFlavorSlot1.$product, VARIATION_DATA_KEYS.secondary);
    const r2 = parseVariantAttr(dualFlavorSlot2.$product, VARIATION_DATA_KEYS.secondary);
    if (!r1 || !r2) return null;
    return (r1.priceInCents + r2.priceInCents) / PRICING.centsPerShekel;
  }

  function updateDualFlavorDom() {
    $(SELECTORS.resultsProdsProdRow).removeClass(CLS.flavorRow1).removeClass(CLS.flavorRow2);
    if (dualFlavorSlot1 && dualFlavorSlot1.$product && dualFlavorSlot1.$product.length) {
      dualFlavorSlot1.$product.parents(SELECTORS.prodRow).addClass(CLS.flavorRow1);
    }
    if (dualFlavorSlot2 && dualFlavorSlot2.$product && dualFlavorSlot2.$product.length) {
      dualFlavorSlot2.$product.parents(SELECTORS.prodRow).addClass(CLS.flavorRow2);
    }
    $(SELECTORS.dualFlavor1Title).text(
      dualFlavorSlot1 ? getProductTitleFromRow(dualFlavorSlot1.$product) : "—"
    );
    $(SELECTORS.dualFlavor2Title).text(
      dualFlavorSlot2 ? getProductTitleFromRow(dualFlavorSlot2.$product) : "—"
    );
  }

  function setDualFlavorCheckoutButtonsDisabled(disabled) {
    if ($cache.addToCart && $cache.addToCart.length) {
      $cache.addToCart.prop("disabled", disabled);
      $cache.addToCart.attr("aria-disabled", disabled ? "true" : "false");
    }
    if ($cache.quizResultsAdd2Btn && $cache.quizResultsAdd2Btn.length) {
      $cache.quizResultsAdd2Btn.prop("disabled", disabled);
      $cache.quizResultsAdd2Btn.attr("aria-disabled", disabled ? "true" : "false");
    }
  }

  function updateDualFlavorPricingAndButton() {
    const complete = !!(dualFlavorSlot1 && dualFlavorSlot2);
    if (isDualFlavorSubscriptionMode()) {
      if (complete) {
        const subShek = getDualFlavorSubscriptionShekelsTotal();
        const origShek = getDualFlavorOriginalShekelsTotal();
        if (subShek != null && origShek != null) {
          renderPricesFromSubscriptionPair(origShek, subShek);
        }
        const sub1 = getSubscriptionVariantForPack(dualFlavorSlot1.$product, "secondary");
        if (sub1 && sub1.id) {
          $cache.buyVariationId.val(String(sub1.id));
        }
      }
      setDualFlavorCheckoutButtonsDisabled(!complete);
    } else {
      setDualFlavorCheckoutButtonsDisabled(false);
    }
  }

  function getDualFlavorSubscriptionCartPayload() {
    if (!$(SELECTORS.wrapper).length) return null;
    if (!isSubscriptionActive() || !isDualFlavorSubscriptionMode()) return null;
    if (!dualFlavorSlot1 || !dualFlavorSlot2) return false;
    const sub1 = getSubscriptionVariantForPack(dualFlavorSlot1.$product, "secondary");
    const sub2 = getSubscriptionVariantForPack(dualFlavorSlot2.$product, "secondary");
    if (!sub1 || !sub1.id || !sub2 || !sub2.id) return false;
    syncSubscriptionLineItemProperties();
    const props = {};
    const desc = $cache.subscriptionDescriptionInput.val();
    const flag = $cache.subscriptionFlagInput.val();
    const freq = $cache.subscriptionFrequencyProp.val();
    if (desc) props["תיאור"] = String(desc);
    if (flag) props["_subscription"] = String(flag);
    if (freq) props["תדירות"] = String(freq);
    if (quizDogId) props[LINE_ITEM_PROPERTIES.dogId] = String(quizDogId);
    const quizDataPropertyValue = buildQuizDataPropertyValue();
    if (quizDataPropertyValue) props["_quiz_data"] = quizDataPropertyValue;
    props["כמות טעמים"] = "2";
    return {
      items: [
        { id: String(sub1.id), quantity: 1, properties: props },
        { id: String(sub2.id), quantity: 1, properties: props },
      ],
    };
  }

  function handleDualFlavorProductClick($clickedProduct) {
    const handle = $clickedProduct.attr(DATA.attrHandle);
    if (!handle) return;
    $cache.htbWrapper.removeClass(CLS.hideOnD);
    $cache.sidesLeftTitle.removeClass(CLS.hideOnD);
    const h1 = dualFlavorSlot1 ? dualFlavorSlot1.handle : null;
    const h2 = dualFlavorSlot2 ? dualFlavorSlot2.handle : null;
    if (h1 && handle === h1) {
      dualFlavorSlot1 = null;
      dualFlavorSlot2 = null;
    } else if (h2 && handle === h2) {
      dualFlavorSlot2 = null;
    } else if (!h1) {
      dualFlavorSlot1 = { handle, $product: $clickedProduct };
    } else if (!h2 && handle !== h1) {
      dualFlavorSlot2 = { handle, $product: $clickedProduct };
    } else if (h1 && h2 && handle !== h1 && handle !== h2) {
      dualFlavorSlot2 = { handle, $product: $clickedProduct };
    } else if (h1 && !h2 && handle === h1) {
      return;
    }
    $(SELECTORS.resultsProdsProdRow).removeClass(CLS.active);
    $(SELECTORS.resultsProdsSampleRow).removeClass(CLS.active);
    updateDualFlavorDom();
    updateDualFlavorPricingAndButton();
    syncSubscriptionLineItemProperties();
  }

  /**
   * Renders subscription row + CTA from regular (strikethrough) vs subscription product monthly shekels.
   * @param {number} origShekels
   * @param {number} subShekels
   * @returns {void}
   */
  function renderPricesFromSubscriptionPair(origShekels, subShekels) {
    const originalDaily = (origShekels / PRICING.daysInMonth).toFixed(2);
    const subDaily = (subShekels / PRICING.daysInMonth).toFixed(2);
    $cache.buyPrice.text(shekel(subShekels.toFixed(0)));
    $cache.subMonthlyPrice.text(shekel(subShekels.toFixed(0)) + FORMAT.monthlySuffix);
    $cache.subDailyPrice.text(shekel(subDaily) + FORMAT.dailySuffix);
    $cache.originalMonthlyPrice.text(shekel(origShekels.toFixed(0)));
    $cache.originalDailyPrice.text(shekel(originalDaily));
  }

  /**
   * Updates subscription price labels from selected pack and subscription map (or falls back to 10% off regular).
   * @param {jQuery} $product
   * @returns {void}
   */
  function updateSubscriptionPriceDisplay($product) {
    if (!isSubscriptionActive() || !$product || !$product.length) return;
    const pack = getActiveSubscriptionPack();
    const reg =
      pack === "secondary"
        ? parseVariantAttr($product, VARIATION_DATA_KEYS.secondary)
        : parseVariantAttr($product, VARIATION_DATA_KEYS.primary);
    if (!reg) return;
    const origShekels = reg.priceInCents / PRICING.centsPerShekel;
    const subEntry = getSubscriptionVariantForPack($product, pack);
    if (subEntry && isFinite(subEntry.price)) {
      const subShekels = subEntry.price / PRICING.centsPerShekel;
      renderPricesFromSubscriptionPair(origShekels, subShekels);
    } else {
      renderPrices(computePrices(origShekels, true), PRICE_RENDER_SCOPE.all);
    }
  }

  /**
   * Applies pricing to buy wrapper for the current product and purchase mode.
   * @param {jQuery} $product
   * @param {boolean} isSubscriptionSelected
   * @returns {void}
   */
  function applyBuyWrapperPricing($product, isSubscriptionSelected) {
    const primary = parseVariantAttr($product, VARIATION_DATA_KEYS.primary);
    if (!primary) return;
    const shekels = primary.priceInCents / PRICING.centsPerShekel;
    if (isSubscriptionSelected && hasSubscriptionMapping($product)) {
      updateSubscriptionPriceDisplay($product);
    } else {
      renderPrices(computePrices(shekels, isSubscriptionSelected), PRICE_RENDER_SCOPE.all);
    }
  }

  /**
   * Sets hidden `id` for add-to-cart: subscription product variant when in subscription mode, else one-time qty.
   * @param {jQuery} $product
   * @returns {void}
   */
  function applyCartVariantId($product) {
    if (!$product || !$product.length) return;
    if (isDualFlavorSubscriptionMode() && dualFlavorSlot1 && dualFlavorSlot2) {
      updateDualFlavorPricingAndButton();
      return;
    }
    if (isSubscriptionActive()) {
      const pack = getActiveSubscriptionPack();
      const sub = getSubscriptionVariantForPack($product, pack);
      if (sub && sub.id !== undefined && sub.id !== null && String(sub.id).trim() !== "") {
        $cache.buyVariationId.val(String(sub.id));
        return;
      }
      const primary = parseVariantAttr($product, VARIATION_DATA_KEYS.primary);
      if (primary) {
        $cache.buyVariationId.val(String(primary.id));
      }
      return;
    }
    const $activeBtn = $(SELECTORS.qtyButtons + "." + CLS.active);
    let id = $activeBtn.attr(DATA.attrId);
    if (!id) {
      const primary = parseVariantAttr($product, VARIATION_DATA_KEYS.primary);
      if (primary) id = primary.id;
    }
    if (id) $cache.buyVariationId.val(String(id));
  }

  /**
   * Labels subscription size buttons and toggles secondary visibility from row data.
   * @param {jQuery} $product
   * @returns {void}
   */
  function configureSubscriptionButtonsFromProduct($product) {
    const primary = parseVariantAttr($product, VARIATION_DATA_KEYS.primary);
    const secondary = parseVariantAttr($product, VARIATION_DATA_KEYS.secondary);
    if (primary && primary.qtyTitle) {
      $cache.subscriptionSizeBtnPrimary.text(primary.qtyTitle).show();
    }
    if (secondary && secondary.qtyTitle) {
      $cache.subscriptionSizeBtnSecondary.text(secondary.qtyTitle).show();
    } else {
      $cache.subscriptionSizeBtnSecondary.hide();
    }
    $cache.subscriptionSizeBtnSecondary.removeClass(CLS.active);
    $cache.subscriptionSizeBtnPrimary.addClass(CLS.active);
  }

  /**
   * Sets data-id, data-price (shekels), and label text on a buy-wrapper qty button.
   * @param {jQuery} $optionButton
   * @param {{ id: *, priceInCents: number, qtyTitle: string }} parsed
   * @returns {void}
   */
  function setBuyWrapperOption($optionButton, parsed) {
    $optionButton
      .attr(DATA.attrId, parsed.id)
      .attr(DATA.attrPrice, (parsed.priceInCents / PRICING.centsPerShekel).toFixed(0))
      .text(parsed.qtyTitle);
  }

  /**
   * Clears the second qty button so stale labels from a previous product cannot persist.
   * @returns {void}
   */
  function clearBuyWrapperOptionTwo() {
    $cache.buyOption2.removeAttr(DATA.attrId).removeAttr(DATA.attrPrice).text("");
  }

  /**
   * Fills buy wrapper from Shopify variant objects (first recommendation load).
   * @param {jQuery} $product
   * @param {object} primaryVariant
   * @param {object} [secondaryVariant]
   * @param {boolean} isSubscriptionSelected
   * @returns {void}
   */
  function fillBuyWrapperFromVariants($product, primaryVariant, secondaryVariant, isSubscriptionSelected) {
    const primaryParsed = {
      id: primaryVariant.id,
      priceInCents: primaryVariant.price,
      qtyTitle: primaryVariant.qtyTitle,
      sku: primaryVariant.sku || "",
    };
    setBuyWrapperOption($cache.buyOption1, primaryParsed);

    if (
      secondaryVariant &&
      secondaryVariant.available &&
      secondaryVariant.grams === primaryVariant.grams
    ) {
      setBuyWrapperOption($cache.buyOption2, {
        id: secondaryVariant.id,
        priceInCents: secondaryVariant.price,
        qtyTitle: secondaryVariant.qtyTitle,
        sku: secondaryVariant.sku || "",
      });
    } else {
      clearBuyWrapperOptionTwo();
    }

    configureSubscriptionButtonsFromProduct($product);
    applyBuyWrapperPricing($product, isSubscriptionSelected);
    applyCartVariantId($product);
  }

  /**
   * Populates a visible product row and optionally the buy wrapper for the first recommendation.
   * @param {jQuery} $product
   * @param {object} primaryVariant
   * @param {object} [secondaryVariant]
   * @param {number} recommendedProductCount
   * @returns {void}
   */
  function updateRowFromVariant($product, primaryVariant, secondaryVariant, recommendedProductCount) {
    writeVariantToRow($product, primaryVariant, secondaryVariant);

    if (recommendedProductCount === 1 && !$cache.samplePack.length) {
      fillBuyWrapperFromVariants($product, primaryVariant, secondaryVariant, isSubscriptionActive());
    }
  }

  /**
   * Builds the hidden contact field payload summarizing quiz answers and calories.
   * @param {number} calories
   * @param {object} parsedQuizData From loadQuizDataFromFlat() only.
   * @returns {string}
   */
  function buildContactSummary(calories, parsedQuizData) {
    const proteinPart =
      parsedQuizData.protein === quizValues.NO_EXISTS ? parsedQuizData.protein : parsedQuizData.protein_desc;
    const sensitivitiesPart =
      parsedQuizData.sensitivities === quizValues.NO_EXISTS
        ? parsedQuizData.sensitivities
        : parsedQuizData.sensitivities_desc;

    return (
      FORMAT.contactFormCaloriesPrefix +
      calories +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormNamePrefix +
      parsedQuizData.name +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormGenderPrefix +
      parsedQuizData.gender +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormTypePrefix +
      parsedQuizData.type +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormAgePrefix +
      parsedQuizData.age +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormWeightPrefix +
      parsedQuizData.weight +
      FORMAT.weightUnitSuffix +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormCastrationPrefix +
      parsedQuizData.castration +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormActivityPrefix +
      parsedQuizData.activity +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormBodyPrefix +
      parsedQuizData.body +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormProteinPrefix +
      proteinPart +
      FORMAT.contactFormFieldSep +
      FORMAT.contactFormSensitivitiesPrefix +
      sensitivitiesPart
    );
  }

  /**
   * Switches the page to the no-recommendations state with a contact form.
   * @param {number} calories
   * @param {object} parsedQuizData
   * @returns {void}
   */
  function handleNoRecommendations(calories, parsedQuizData) {
    $(SELECTORS.resultsProds).hide();
    const titleSuffix =
      parsedQuizData.gender === quizValues.GENDER_MALE
        ? TEXT.noRecTitleMaleSuffix
        : TEXT.noRecTitleFemaleSuffix;
    $(SELECTORS.resultsTitle).text(parsedQuizData.name + titleSuffix);
    $(SELECTORS.resultsSubtitle).html(TEXT.noRecSubtitleHtml);
    $(SELECTORS.contactFormQuizData).val(buildContactSummary(calories, parsedQuizData));
    $(SELECTORS.resultsFormWrapper).addClass(CLS.show);
    $(SELECTORS.resultsStickyGoto).hide();
  }

  /**
   * Returns true if the row carries any excluded collection tag.
   * @param {jQuery} $row
   * @returns {boolean}
   */
  function rowHasExcludedTag($row) {
    for (let i = 0; i < FILTER.excludedTags.length; i++) {
      if ($row.hasClass(FILTER.tagPrefix + FILTER.excludedTags[i])) return true;
    }
    return false;
  }

  /**
   * True when the row is in the dog collection for a dog quiz.
   * @param {jQuery} $row
   * @param {string} petType
   * @returns {boolean}
   */
  function matchesPetCollection($row, petType) {
    return petType === quizValues.PET_TYPE_DOG && $row.hasClass(FILTER.dogCollectionClass);
  }

  /**
   * Weight-based tag gates (micro-grain, super-food).
   * @param {jQuery} $row
   * @param {number} weight
   * @returns {boolean}
   */
  function matchesWeightTagRules($row, weight) {
    if (weight <= FILTER.microGrainMaxWeight && !$row.hasClass(FILTER.tagPrefix + FILTER.microGrainTag)) {
      return false;
    }
    if (weight > FILTER.microGrainExcludeAbove && $row.hasClass(FILTER.tagPrefix + FILTER.microGrainTag)) {
      return false;
    }
    if (weight < FILTER.superFoodMinWeight && $row.hasClass(FILTER.tagPrefix + FILTER.superFoodTag)) {
      return false;
    }
    return true;
  }

  /**
   * Age-based tag gate (seniors product line).
   * @param {jQuery} $row
   * @param {number} age
   * @returns {boolean}
   */
  function matchesAgeTagRules($row, age) {
    if (age < FILTER.seniorsMinAge && $row.hasClass(FILTER.tagPrefix + FILTER.seniorsTag)) {
      return false;
    }
    return true;
  }

  /**
   * True if the row does not contain any tag the user is sensitive to.
   * @param {jQuery} $row
   * @param {string[]} sensitivitiesList
   * @returns {boolean}
   */
  function matchesSensitivities($row, sensitivitiesList) {
    for (let i = 0; i < sensitivitiesList.length; i++) {
      if ($row.hasClass(FILTER.tagPrefix + sensitivitiesList[i])) return false;
    }
    return true;
  }

  /**
   * True if the row matches protein preference or best-seller fallback when no preference list.
   * @param {jQuery} $row
   * @param {string[]} proteinPreferenceList
   * @returns {boolean}
   */
  function matchesProteinPreference($row, proteinPreferenceList) {
    if (proteinPreferenceList.length > 0) {
      for (let i = 0; i < proteinPreferenceList.length; i++) {
        if ($row.hasClass(FILTER.tagPrefix + proteinPreferenceList[i])) return true;
      }
      return false;
    }
    return $row.hasClass(FILTER.tagPrefix + FILTER.bestSellerTag);
  }

  /**
   * Determines whether a product row should be shown for the given quiz answers.
   * @param {jQuery} $row
   * @param {object} quizData
   * @param {string[]} sensitivitiesList
   * @param {string[]} proteinPreferenceList
   * @returns {boolean}
   */
  function shouldShowProductRow($row, quizData, sensitivitiesList, proteinPreferenceList) {
    if (!matchesPetCollection($row, quizData.type)) return false;
    if (!matchesWeightTagRules($row, quizData.weight)) return false;
    if (!matchesAgeTagRules($row, quizData.age)) return false;
    if (rowHasExcludedTag($row)) return false;
    if (!matchesSensitivities($row, sensitivitiesList)) return false;
    if (!matchesProteinPreference($row, proteinPreferenceList)) return false;
    return true;
  }

  /**
   * Resolves buy-wrapper state from a selected .prod element (parsed variants + subscription flag).
   * @param {jQuery} $product
   * @returns {{ primary: object|null, secondary: object|null, isSubscription: boolean }}
   */
  function resolveBuyWrapperState($product) {
    return {
      primary: parseVariantAttr($product, VARIATION_DATA_KEYS.primary),
      secondary: parseVariantAttr($product, VARIATION_DATA_KEYS.secondary),
      isSubscription: isSubscriptionActive(),
    };
  }

  /**
   * Applies resolved buy-wrapper state to the DOM (qty buttons, prices, active classes).
   * @param {{ primary: object|null, secondary: object|null, isSubscription: boolean }} state
   * @returns {void}
   */
  function renderBuyWrapper($product, state) {
    if (state.primary) {
      setBuyWrapperOption($cache.buyOption1, state.primary);
    }
    if (state.secondary) {
      setBuyWrapperOption($cache.buyOption2, state.secondary);
    } else {
      clearBuyWrapperOptionTwo();
    }
    $cache.buyOption1.addClass(CLS.active);
    $cache.buyOption2.removeClass(CLS.active);
    configureSubscriptionButtonsFromProduct($product);
    applyBuyWrapperPricing($product, state.isSubscription);
    applyCartVariantId($product);
  }

  /**
   * Syncs buy-wrapper from the selected product row.
   * @param {jQuery} $product
   * @returns {void}
   */
  function syncBuyWrapperFromProduct($product) {
    renderBuyWrapper($product, resolveBuyWrapperState($product));
  }

  /**
   * Fills calculator summary fields from quiz data and computed calories.
   * @param {object} parsedQuizData
   * @param {number} calories
   * @returns {void}
   */
  function fillCalculatorSummary(parsedQuizData, calories) {
    $(SELECTORS.calculatName).text(parsedQuizData.name);
    $(SELECTORS.calculatType).text(TEXT.petTypePrefix + parsedQuizData.type);
    $(SELECTORS.calculatAge).text(parsedQuizData.age);
    $(SELECTORS.calculatWeight).text(parsedQuizData.weight + FORMAT.weightUnitSuffix);
    $(SELECTORS.calculatCals).text(calories);
  }

  /**
   * Parses sensitivities and protein preference CSV fields from quiz data (via engine helpers).
   * @param {object} parsedQuizData
   * @returns {{ sensitivitiesList: string[], proteinPreferenceList: string[] }}
   */
  function parseFilterTags(parsedQuizData) {
    const sensitivitiesRaw =
      parsedQuizData.sensitivities === quizValues.YES_EXISTS ? parsedQuizData.sensitivities_desc || "" : "";
    const proteinRaw =
      parsedQuizData.protein === quizValues.YES_EXISTS ? parsedQuizData.protein_desc || "" : "";
    return {
      sensitivitiesList: quizEngine.splitCsvTrim(sensitivitiesRaw),
      proteinPreferenceList: quizEngine.splitCsvTrim(proteinRaw),
    };
  }

  /**
   * Clears and sets active row classes for the clicked product block.
   * @param {jQuery} $clickedProduct
   * @returns {void}
   */
  function setActiveRow($clickedProduct) {
    $(SELECTORS.resultsProdsProdRow).removeClass(CLS.active);
    $(SELECTORS.resultsProdsSampleRow).removeClass(CLS.active);
    $clickedProduct.parents(SELECTORS.prodRow).addClass(CLS.active);
    $clickedProduct.parents(SELECTORS.sampleRow).addClass(CLS.active);
  }

  /**
   * Sets active class on the clicked purchase-mode option and clears siblings.
   * @param {jQuery} $clicked
   * @returns {void}
   */
  function setActivePurchaseMode($clicked) {
    $cache.wrapper.find(SELECTORS.dataOption).removeClass(CLS.active);
    $clicked.addClass(CLS.active);
  }

  /**
   * Resolves selection context from a clicked product node.
   * @param {jQuery} $clickedProduct
   * @returns {{ isSamplePack: boolean }}
   */
  function resolveProductSelection($clickedProduct) {
    return {
      isSamplePack: $clickedProduct.hasClass(CLS.samplePack),
    };
  }

  /**
   * Renders product selection UI (active rows, sample pack chrome, cart note).
   * @param {{ isSamplePack: boolean }} resolved
   * @param {jQuery} $clickedProduct
   * @param {object} parsedQuizData
   * @returns {void}
   */
  function renderProductSelection(resolved, $clickedProduct, parsedQuizData) {
    setActiveRow($clickedProduct);
    if (resolved.isSamplePack) {
      applySamplePackSelectionUi($clickedProduct, parsedQuizData);
    } else {
      $cache.htbWrapper.removeClass(CLS.hideOnD);
      $cache.sidesLeftTitle.removeClass(CLS.hideOnD);
    }
  }

  /**
   * Applies sample-pack-specific buy-wrapper and cart note updates.
   * @param {jQuery} $clickedProduct
   * @param {object} parsedQuizData
   * @returns {void}
   */
  function applySamplePackSelectionUi($clickedProduct, parsedQuizData) {
    $cache.htbWrapper.addClass(CLS.hideOnD);
    $cache.sidesLeftTitle.addClass(CLS.hideOnD);
    $cache.buyPrice.text(shekel(parseFloat($clickedProduct.attr(DATA.attrPrice))));
    $cache.buyVariationId.val($clickedProduct.attr(DATA.attrId));
    $(SELECTORS.cartNote).val(
      FORMAT.cartNoteSensitivitiesPrefix +
        parsedQuizData.sensitivities +
        FORMAT.cartNoteNewline +
        parsedQuizData.sensitivities_desc
    );
  }

  /**
   * Updates active row state and sample-specific UI after a product click.
   * @param {jQuery} $clickedProduct
   * @param {object} parsedQuizData
   * @returns {void}
   */
  function selectProduct($clickedProduct, parsedQuizData) {
    renderProductSelection(resolveProductSelection($clickedProduct), $clickedProduct, parsedQuizData);
  }

  /**
   * Iterates product rows: shows matches, loads variants, tracks recommendation count.
   * @param {object} parsedQuizData
   * @param {number} calories
   * @param {{ sensitivitiesList: string[], proteinPreferenceList: string[] }} tagLists
   * @returns {number} Number of recommended rows shown.
   */
  function filterAndFetchProducts(parsedQuizData, calories, tagLists) {
    let recommendedProductCount = 0;
    const pending = [];

    $(SELECTORS.prodRow).each(function () {
      const $row = $(this);
      const $prod = $row.find(SELECTORS.prod);

      if (shouldShowProductRow($row, parsedQuizData, tagLists.sensitivitiesList, tagLists.proteinPreferenceList)) {
        recommendedProductCount++;
        pending.push(fetchAndApplyBestVariant($prod, recommendedProductCount, calories));
        if (recommendedProductCount === 1) $row.addClass(CLS.active);
      } else {
        $row.removeClass(CLS.show);
      }
    });

    // Mills 14.10.2026: count only recipes that were actually shown (a row is counted before its packs are checked).
    return Promise.all(pending).then(function (results) {
      return results.filter(Boolean).length;
    });
  }

  /**
   * Filters products, loads variants for matches, or shows the no-results contact state.
   * @param {object} parsedQuizData
   * @param {number} calories
   * @returns {void}
   */
  /**
   * Mills 14.10.2026: if the first recommended recipe ended up hidden, moves "active" and the buy box to the first shown recipe.
   * @returns {void}
   */
  function ensureActiveShownRow() {
    const $shown = $(SELECTORS.resultsProdsProdRow + "." + CLS.show);
    if (!$shown.length) return;
    if ($shown.filter("." + CLS.active).length) return;
    $(SELECTORS.resultsProdsProdRow).removeClass(CLS.active);
    const $first = $shown.first();
    $first.addClass(CLS.active);
    if (!$cache.samplePack.length) syncBuyWrapperFromProduct($first.find(SELECTORS.prod));
  }

  function runResultsLogic(parsedQuizData, calories) {
    if (calories > quizEngine.CALORIES_NO_REC_MAX) {
      handleNoRecommendations(calories, parsedQuizData);
      return;
    }

    const tagLists = parseFilterTags(parsedQuizData);
    filterAndFetchProducts(parsedQuizData, calories, tagLists).then(function (shownCount) {
      if (shownCount === 0) {
        handleNoRecommendations(calories, parsedQuizData);
        return;
      }
      ensureActiveShownRow();
    });
  }

  /**
   * Toggles expanded ingredient/list content inside a product block.
   * @returns {void}
   */
  function onToggleExpand() {
    const $toggle = $(this);
    const $productBlock = $toggle.parents(SELECTORS.prod);
    if ($productBlock.hasClass(CLS.expanded)) {
      $toggle.text(TEXT.expandList);
      $productBlock.removeClass(CLS.expanded);
    } else {
      $toggle.text(TEXT.collapseList);
      $productBlock.addClass(CLS.expanded);
    }
  }

  /**
   * Returns a delegated click handler for product rows.
   * @param {object} parsedQuizData
   * @returns {function(): void}
   */
  function onProductRowClick(parsedQuizData) {
    return function () {
      const $clickedProduct = $(this);
      if (isDualFlavorSubscriptionMode()) {
        handleDualFlavorProductClick($clickedProduct);
        return;
      }
      selectProduct($clickedProduct, parsedQuizData);
      syncBuyWrapperFromProduct($clickedProduct);
    };
  }

  /**
   * Resolves the shekel price string for purchase-mode toggle (active qty button or active product variant).
   * @returns {string|undefined}
   */
  function getActivePrice() {
    if (isDualFlavorSubscriptionMode() && dualFlavorSlot1 && dualFlavorSlot2) {
      const total = getDualFlavorSubscriptionShekelsTotal();
      if (total != null) return total.toFixed(0);
    }
    const $activeProduct = $(SELECTORS.activeShowProd);
    if (isSubscriptionActive() && $activeProduct.length) {
      if (hasSubscriptionMapping($activeProduct)) {
        const pack = getActiveSubscriptionPack();
        const sub = getSubscriptionVariantForPack($activeProduct, pack);
        if (sub && isFinite(sub.price)) {
          return (sub.price / PRICING.centsPerShekel).toFixed(0);
        }
      }
      const primary = parseVariantAttr($activeProduct, VARIATION_DATA_KEYS.primary);
      if (primary) {
        return (primary.priceInCents / PRICING.centsPerShekel).toFixed(0);
      }
    }
    const $activeQtyButton = $(SELECTORS.qtyButtons + "." + CLS.active);
    let priceValue = $activeQtyButton.attr(DATA.attrPrice);
    if (!priceValue && $activeProduct.length) {
      const primary = parseVariantAttr($activeProduct, VARIATION_DATA_KEYS.primary);
      if (primary) {
        priceValue = (primary.priceInCents / PRICING.centsPerShekel).toFixed(0);
      }
    }
    return priceValue;
  }

  /**
   * Fires GA purchase-option analytics when gtag is available.
   * @param {string} eventCategory
   * @param {string} [eventLabel]
   * @returns {void}
   */
  function fireGtagPurchaseOptionEvent(eventCategory, eventLabel) {
    if (typeof gtag === "undefined") return;
    const payload = { event_category: eventCategory };
    if (eventLabel !== undefined) payload.event_label = eventLabel;
    gtag("event", GTAG.eventName, payload);
  }

  /**
   * Handles subscription vs one-time option clicks: active state, prices, UI sections, analytics.
   * @returns {void}
   */
  function onPurchaseModeClick() {
    const $clicked = $(this);
    setActivePurchaseMode($clicked);
    const isSubscriptionMode = $clicked.attr(DATA.attrOption) === DATA.optionSubscription;
    if (!isSubscriptionMode) {
      clearDualFlavorSlots();
      $(SELECTORS.resultsProdsProdRow).removeClass(CLS.flavorRow1).removeClass(CLS.flavorRow2);
    }
    setSubscriptionMode(isSubscriptionMode);

    const $activeProduct = $(SELECTORS.activeShowProd);
    if ($activeProduct.length) {
      if (isSubscriptionMode) {
        updateSubscriptionPriceDisplay($activeProduct);
      } else {
        const priceValue = getActivePrice();
        if (priceValue !== undefined) {
          renderPrices(computePrices(priceValue, false), PRICE_RENDER_SCOPE.toggle);
        }
      }
      applyCartVariantId($activeProduct);
    } else {
      const priceValue = getActivePrice();
      if (priceValue !== undefined) {
        renderPrices(computePrices(priceValue, isSubscriptionMode), PRICE_RENDER_SCOPE.toggle);
      }
    }
    syncSubscriptionLineItemProperties();
    updateDualFlavorPricingAndButton();

    if (isSubscriptionMode) {
      fireGtagPurchaseOptionEvent(GTAG.subscriptionCategory);
    }
  }

  /**
   * Activates a quantity button and deactivates siblings.
   * @param {jQuery} $button
   * @returns {void}
   */
  function setActiveQtyButton($button) {
    $button.siblings(SELECTORS.qtyButton).removeClass(CLS.active);
    $button.addClass(CLS.active);
  }

  /**
   * Handles quantity button clicks: selection state, analytics, price, variant id.
   * @returns {void}
   */
  function onQtyButtonClick() {
    const $btn = $(this);
    const price = $btn.attr(DATA.attrPrice);
    const id = $btn.attr(DATA.attrId);
    setActiveQtyButton($btn);
    const buttonText = $btn.text().trim();
    fireGtagPurchaseOptionEvent(GTAG.oneTimeCategory, buttonText);
    renderPrices(computePrices(price, isSubscriptionActive()), PRICE_RENDER_SCOPE.toggle);
    $cache.buyVariationId.val(id);
  }

  /**
   * Smooth-scrolls the viewport toward the sidebar column anchor.
   * @returns {void}
   */
  function onScrollToResults() {
    $("html,body").animate(
      { scrollTop: $(SELECTORS.sidesLeft).offset().top - LAYOUT.scrollToSidebarOffset },
      SCROLL_ANIMATION.durationMs,
      SCROLL_ANIMATION.easing
    );
  }

  /**
   * Attaches delegated click handlers for results page interactions.
   * @param {object} parsedQuizData
   * @returns {void}
   */
  function onSubscriptionSizeClick() {
    const $btn = $(this);
    $btn.siblings(".subscription-size-btn").removeClass(CLS.active);
    $btn.addClass(CLS.active);

    const pack = getActiveSubscriptionPack();
    const secondaryVisible = $cache.subscriptionSizeBtnSecondary.is(":visible");

    if (pack === "secondary" && secondaryVisible) {
      const $prevActive = $(SELECTORS.activeShowProd);
      $(SELECTORS.resultsProdsProdRow).removeClass(CLS.active);
      $(SELECTORS.resultsProdsSampleRow).removeClass(CLS.active);
      clearDualFlavorSlots();
      if ($prevActive.length) {
        dualFlavorSlot1 = { handle: $prevActive.attr(DATA.attrHandle), $product: $prevActive };
        $prevActive.parents(SELECTORS.prodRow).addClass(CLS.active);
      }
      updateDualFlavorDom();
      updateDualFlavorBlockVisibility();
      updateDualFlavorPricingAndButton();
      syncSubscriptionLineItemProperties();
      return;
    }

    const preferFirst = dualFlavorSlot1 && dualFlavorSlot1.$product ? dualFlavorSlot1.$product : null;
    clearDualFlavorSlots();
    $(SELECTORS.resultsProdsProdRow).removeClass(CLS.flavorRow1).removeClass(CLS.flavorRow2);
    $(SELECTORS.resultsProdsProdRow).removeClass(CLS.active);
    $(SELECTORS.resultsProdsSampleRow).removeClass(CLS.active);
    if (preferFirst && preferFirst.length) {
      preferFirst.parents(SELECTORS.prodRow).addClass(CLS.active);
    } else {
      const $first = $(SELECTORS.resultsProdsProdRow + "." + CLS.show).first();
      if ($first.length) $first.addClass(CLS.active);
    }

    const $activeProduct = $(SELECTORS.activeShowProd);
    if ($activeProduct.length) {
      updateSubscriptionPriceDisplay($activeProduct);
      applyCartVariantId($activeProduct);
    }
    updateDualFlavorBlockVisibility();
    updateDualFlavorPricingAndButton();
    syncSubscriptionLineItemProperties();
  }

  function onSubscriptionFrequencyClick() {
    const $btn = $(this);
    const val = $btn.attr(DATA.attrFrequencyValue);
    if (val) $cache.subscriptionFrequencyValue.val(val);
    $btn.siblings(".subscription-frequency-btn").removeClass(CLS.active);
    $btn.addClass(CLS.active);
    syncSubscriptionLineItemProperties();
  }

  function bindResultsEvents(parsedQuizData) {
    $("body").on("click", SELECTORS.openClose, onToggleExpand);
    $("body").on("click", SELECTORS.resultsProdsProd, onProductRowClick(parsedQuizData));
    $("body").on("click", SELECTORS.dataOption, onPurchaseModeClick);
    $("body").on("click", SELECTORS.qtyButtons, onQtyButtonClick);
    $("body").on("click", SELECTORS.subscriptionSizeButtons, onSubscriptionSizeClick);
    $("body").on("click", SELECTORS.subscriptionFrequencyButtons, onSubscriptionFrequencyClick);
    $("body").on("click", SELECTORS.resultsStickyGoto, onScrollToResults);
    $cache.productFormMain.on("submit", syncSubscriptionLineItemProperties);
  }

  /**
   * Loads quiz data from localStorage (flat keys), applies UI, filters products, binds events.
   * @returns {void}
   */
  function init() {
    if ($(SELECTORS.wrapper).length === 0) return;

    cacheStaticDom();
    loadQuizDogIdFromStorage();
    setSubscriptionMode(isSubscriptionActive());
    syncSubscriptionLineItemProperties();

    const parsedQuizData = quizEngine.loadQuizDataFromFlat();
    if (!parsedQuizData) {
      $cache.headlineOnRight.removeClass(CLS.headlineLoading);
      return;
    }

    createDogFromQuizData(parsedQuizData).then(function () {
      syncSubscriptionLineItemProperties();
    });

    const calories = quizEngine.computeCalories(parsedQuizData);
    fillCalculatorSummary(parsedQuizData, calories);
    loadSubscriptionMap().then(function () {
      runResultsLogic(parsedQuizData, calories);
    });

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        $cache.headlineOnRight.removeClass(CLS.headlineLoading);
      });
    });

    bindResultsEvents(parsedQuizData);

    window.MillsResultsGetSubscriptionCartPayload = getDualFlavorSubscriptionCartPayload;
    updateDualFlavorBlockVisibility();
    updateDualFlavorPricingAndButton();
  }

  jQuery(document).ready(init);
})();
